import { useIntl } from 'react-intl';
import { RenderableVolume } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { ImageLayer, Plane, ViewportCell } from '../layout/types';
import SecondaryImagePlaceholder from '../tile/SecondaryImagePlaceholder';
import Tile from '../tile/Tile';
import { PlaneCycleControl } from '../layout/planeCycle';
import {
    resolvePaneHistograms,
    resolveRowFrame,
    RowBlendControls,
} from '../tile/tileLink';
import {
    paneAccessibleName,
    paneNamesItsImage,
    imagesDrawnByPane,
    seriesNumberText,
    fusedImageNamedByPane,
    shownImageOfPane,
} from '../tile/tileViewModel';
import DraggableCell from './DraggableCell';
import { imagesLedByPane } from './imagesLedByPane';
import { RowHost } from './RowHost';

interface DraggableTileCellProps {
    cell: ViewportCell;
    plane: Plane;
    /** Which of the row's fused pair the pane draws. */
    layer: ImageLayer;
    volume: RenderableVolume;
    /** The row's blend controls, so a secondary pane knows whether it has one. */
    blend: RowBlendControls;
    /** The pane's 0-based address, for its accessible name and its drop rule. */
    rowIndex: number;
    columnIndex: number;
    /** Whether this is the row's first image pane: the one that stands for it. */
    leadsRow: boolean;
    /** What each image pane before this one in the row's reading order draws. */
    earlierLayers: ReadonlyArray<ImageLayer>;
    /** The row's plane control, on the pane that offers it. */
    planeCycle?: PlaneCycleControl;
    host: RowHost;
}

/**
 * One image pane of a bound row: the tile, draggable by its title bar to
 * reorder within the row, and a drop target for its row's other panes. A
 * pane asked to draw a secondary the row does not have shows the
 * placeholder that offers the picker instead.
 */
export default function DraggableTileCell({
    cell,
    plane,
    layer,
    volume,
    blend,
    rowIndex,
    columnIndex,
    leadsRow,
    earlierLayers,
    planeCycle,
    host,
}: DraggableTileCellProps) {
    useChangeVersion(blend.source);
    const intl = useIntl();
    const renderer = host.rendererFor(cell.id);
    const fused = blend.source.getCurrent();
    const shownImage = shownImageOfPane(layer, volume.metadata, fused);
    if (!shownImage) {
        return (
            <SecondaryImagePlaceholder
                source={blend.source}
                onFuse={blend.onFuse}
                onClear={blend.onClear}
            />
        );
    }
    const drawnImages = imagesDrawnByPane(
        layer,
        volume.metadata,
        fused,
        (seriesNumber) => seriesNumberText(intl, seriesNumber)
    );
    return (
        <DraggableCell
            cell={cell}
            name={paneAccessibleName(intl, shownImage, plane)}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            host={host}
        >
            {(dragHandleProps) => (
                <Tile
                    volume={volume}
                    plane={plane}
                    layer={layer}
                    shownImage={shownImage}
                    fusedImage={fusedImageNamedByPane(layer, fused)}
                    namesItsImage={paneNamesItsImage(layer, leadsRow)}
                    listedImages={host.binding.availableDatasets}
                    renderer={renderer}
                    frame={resolveRowFrame(
                        volume.metadata.seriesInstanceUid,
                        host.environmentAdapter
                    )}
                    isActive={host.activeId === cell.id}
                    onSelectRoi={host.roiSelectionFor(volume)}
                    structureActions={host.structureActionsFor(volume)}
                    overlay={host.overlayFor(
                        renderer,
                        imagesLedByPane(
                            layer,
                            earlierLayers,
                            volume.metadata,
                            fused
                        )
                    )}
                    status={host.statusFor({ images: drawnImages, leadsRow })}
                    imageDetails={host.imageDetails}
                    drawnImages={drawnImages}
                    onMaximize={() => host.onToggleMaximize(cell.id)}
                    planeCycle={planeCycle}
                    scaleBarVisible={host.scaleBarVisible}
                    windowHistograms={resolvePaneHistograms(
                        drawnImages,
                        volume.metadata,
                        fused,
                        blend.source.getBlend(),
                        host.windowingAdapter
                    )}
                    dragHandleProps={dragHandleProps}
                />
            )}
        </DraggableCell>
    );
}
